import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/DeviceDeployment.css';
import CustomButton from '../CustomElements/CustomButton';
import Selector from '../CustomElements/Selector';
import DateSelector from '../CustomElements/DateSelector';
import CustomTitle from '../CustomElements/CustomTitle';
import CustomTextarea from '../CustomElements/CustomTextarea';
import FormContainer from '../CustomElements/FormContainer';
import CustomContainer from '../CustomElements/CustomContainer';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import FilePicker from '../FileManager/FilePicker';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
import useFormDataChanged from '../../customHook/useFormDataChanged';
import useOptionsLoader from '../../customHook/useOptionsLoader';
import useSuccessListener from '../../customHook/useSuccessListener';
// Утилиты
import { convertToDate, transformFieldsToMoscowStart } from '../../utils/convertToDate';
import { omitFields } from '../../utils/omitFields';
import { handleUniversalChange } from '../../utils/formUtils';
import { prepareObjectForSubmit } from '../../utils/prepareObjectForSubmit';
// Действия Redux
import { editOperationStatusFromServer } from '../../redux/actions/statuses.action';
import { initOptionsFromServer } from '../../redux/actions/options.action';
import { load } from '../../redux/actions/loader.action';
import {
  initDepartmentOptionsFromServer,
  initOptionsBrigadesFromServer,
  initOptionsDirectoratesFromServer,
  initOptionsEmployeesFromServer,
} from '../../redux/actions/options.action';

//компонент формы изменения статуса выдачи устройства в эксплуатацию

function EditUsageSendStatus({ currentDevice, closeModal, currentStatus, title, isEdit, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();
  // Состояния для кнопки и уведомления об успешном добавлении
  const [addSuccess, setAddSuccess] = useState(false);

  // отмена подтверждения
  const closeStatusEditModal = () => {
    setAddSuccess(false);
  };

  // получаем опции из стора
  const { roads, departments, brigadeNames, directorates, workers, employees } = useSelector((state) => state.options);

  // Состояние для формы
  // Мемоизируем начальные данные формы
  const initialFormData = useMemo(
    () => ({
      id: currentStatus?.id,
      typeId: currentDevice?.typeId,
      deviceId: currentDevice.id,
      date: convertToDate(currentStatus.date) || '',
      brigadeNameId: currentStatus?.brigadeNameId || '',
      acceptanceCert: currentStatus?.acceptanceCert || '',
      acceptanceCertName: currentStatus?.acceptanceCertName || '',
      senderId: currentStatus?.senderId || '',
      responsibleContact: currentStatus?.responsibleContact || '',
      responsibleId: currentStatus?.responsibleId || '',
      departmentId: currentStatus?.departmentId || '',
      road: currentStatus?.roadId,
      directorateId: currentStatus?.directorateId || '',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStatus]
  );

  const [formData, setFormData] = useState(initialFormData);

  //хук для запроса опций для дирекций и контроля селекта
  const messageForSelectDirectorates = t?.messageForSelectDirectorates || 'Выберите дорогу';
  const directoratesControl = useOptionsLoader(
    formData,
    setFormData,
    ['directorateId', 'departmentId'],
    'road',
    initOptionsDirectoratesFromServer,
    messageForSelectDirectorates,
    true // <- Пропустить сброс при первом рендере
  );

  //хук для запроса опций для подразделений и контроля селекта
  const messageForSelectDepartments = t?.messageForSelectDepartments || 'Выберите дирекцию';
  const departmentsControl = useOptionsLoader(
    formData,
    setFormData,
    ['responsibleId', 'departmentId'],
    'directorateId',
    initDepartmentOptionsFromServer,
    messageForSelectDepartments,
    true // <- Пропустить сброс при первом рендере
  );
  //хук для запроса опций для ФИО ответственного и контроля селекта
  const messageForSelectWorkers = t?.messageForSelectWorkers || 'Выберите подразделение';
  const workersControl = useOptionsLoader(
    formData,
    setFormData,
    ['responsibleId'],
    'departmentId',
    initOptionsEmployeesFromServer,
    messageForSelectWorkers,
    true // <- Пропустить сброс при первом рендере
  );

  //хук для запроса опций для наименования бригад и контроля селекта
  const messageForSelectBrigades = t?.messageForSelectBrigades || 'Выберите подразделение';
  const brigadesControl = useOptionsLoader(
    formData,
    setFormData,
    ['brigadeNameId'],
    'departmentId',
    initOptionsBrigadesFromServer,
    messageForSelectBrigades,
    true // <- Пропустить сброс при первом рендере
  );

  // Обновляем formData при изменении currentStatus
  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  /** Функция для проверки валидности формы */
  const isButtonDisabled = useButtonValidation(formData, ['responsibleContact']);

  //проверяем были ли изменения в форме
  const isFormEdit = useFormDataChanged(initialFormData, formData, isEdit);

  // Обработчик отправки формы
  const handlerSubmit = (event) => {
    event.preventDefault();
    //переводим в состояние загрузки
    dispatch(load());
    // обрабатываем пустые строки
    const formDataWithEmptyStrings = prepareObjectForSubmit(currentStatus, formData);
    // удаляем поля, которые не нужно отправлять
    const formWithoutExcluded = omitFields(formDataWithEmptyStrings, [
      'id',
      'road',
      'deviceId',
      'acceptanceCertName',
      'directorateId',
    ]);
    // Преобразуем указанные поля в начало дня по Москве
    const payload = transformFieldsToMoscowStart(formWithoutExcluded, ['date']);
    // Отправляем данные на сервер через Redux
    dispatch(
      editOperationStatusFromServer({
        statusId: formData?.id,
        deviceId: formData?.deviceId,
        payload,
        typeId: formData?.typeId,
        path: 'usage',
      })
    );
  };

  //отслеживаем выполнение запроса
  useSuccessListener(setFormData, initialFormData, closeModal, closeStatusEditModal);

  // после закрытия обновляем стейт опций с сервера
  useEffect(() => {
    return () => {
      dispatch(initOptionsFromServer());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Открываем  модальное для подтверждения изменений
  const handlerOpenModal = () => {
    setAddSuccess(true);
  };

  return (
    <CustomContainer>
      <CustomTitle titleText={title} deviceType={currentDevice.type} deviceId={currentDevice.id} />
      {addSuccess && (
        <ConfirmationPage
          confirmText={t?.statusChangeConfirmationText || 'Вы уверены, что хотите изменить данные статуса?'}
          confirmButtonText={t?.confirmButton || 'Изменить'}
          cancelButtonText={t?.cancelButton || 'Отменить'}
          onConfirm={handlerSubmit} // Функция, которая должна быть вызвана при подтверждении
          onCancel={closeStatusEditModal} // Функция для закрытия модального окна или отмены действия
        />
      )}
      {!addSuccess && (
        <FormContainer>
          <DateSelector
            column="left"
            width="100%"
            height="var(--sadr-font-size-60)"
            label={t?.labelIssueDate || 'Дата выдачи в эксплуатацию *'}
            selectedDate={formData.date}
            setSelectedDate={(date) => handleUniversalChange(formData, setFormData, null, 'date', date, 'date')}
            name="date"
            placeholder={t?.placeholderSelectDate || 'Выберите дату'}
            minDate={convertToDate(currentDevice?.lastActionDate)}
            maxDate={new Date()}
          />
          <Selector
            column="left"
            options={employees}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'senderId', selectedOption, 'select');
            }}
            placeholder={t?.placeholderSelectEmployee || 'Выберите ФИО'}
            label={t?.labelEmployeeIssued || 'ФИО выдавшего в эксплуатацию *'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={employees?.find((option) => option.value === formData.senderId)}
            isMulti={false}
            name="senderId"
          />
          <FilePicker
            column="left"
            label={t?.labelAcceptanceCert || 'Акт приема-передачи *'}
            formData={formData}
            setFormData={setFormData}
            fieldName="acceptanceCertName"
            fieldIdName="acceptanceCert"
            rowSpan={2}
          />
          <Selector
            column="right"
            options={roads}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'road', selectedOption, 'select');
            }}
            placeholder={t?.placeholderRoad || 'Выберите дорогу'}
            label={`${t?.labelRoad} *` || 'Дорога *'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={
              formData.road
                ? roads?.find((option) => option.value === formData.road)
                : roads?.find((option) => option.label === formData.road)
            }
            isMulti={false}
            name="road"
          />
          <Selector
            column="right"
            options={directorates}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'directorateId', selectedOption, 'select');
            }}
            placeholder={t?.placeholderDirectorate || 'Выберите дирекцию'}
            label={`${t?.labelDirectorate} *` || 'Дирекция *'}
            value={
              formData.directorateId ? directorates?.find((option) => option.value === formData.directorateId) : null
            }
            isMulti={false}
            name="directorateId"
            width="100%"
            height="var(--sadr-font-size-60)"
            isDisabled={directoratesControl?.isSelectorDisabled}
            message={directoratesControl?.message}
            customClick={directoratesControl?.onSelectorChange}
          />
          <Selector
            column="right"
            options={departments}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'departmentId', selectedOption, 'select');
            }}
            placeholder={t?.placeholderSelectDepartment || 'Выберите подразделение'}
            label={`${t?.labelDepartment} *` || 'Подразделение *'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={
              formData.departmentId && departments
                ? departments?.find((option) => option.value === formData.departmentId)
                : null
            }
            isMulti={false}
            name="departmentId"
            isDisabled={departmentsControl?.isSelectorDisabled}
            message={departmentsControl?.message}
            customClick={departmentsControl?.onSelectorChange}
          />
          <Selector
            column="right"
            options={brigadeNames}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'brigadeNameId', selectedOption, 'select');
            }}
            placeholder={t?.placeholderSelectBrigadeName || 'Выберите наименование бригады'}
            label={t?.labelBrigadeName || 'Наименование бригады *'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={
              formData.brigadeNameId ? brigadeNames?.find((option) => option.value === formData?.brigadeNameId) : null
            }
            isMulti={false}
            name="brigadeNameId"
            isDisabled={brigadesControl?.isSelectorDisabled}
            message={brigadesControl?.message}
            customClick={brigadesControl?.onSelectorChange}
          />
          <Selector
            column="right"
            options={workers}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'responsibleId', selectedOption, 'select');
            }}
            placeholder={t?.placeholderSelectEmployee || 'Выберите ФИО'}
            label={t?.labelResponsibleEmployee || 'ФИО ответственного лица *'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={formData.responsibleId ? workers?.find((option) => option.value === formData.responsibleId) : null}
            isMulti={false}
            name="responsibleId"
            isDisabled={workersControl?.isSelectorDisabled}
            message={workersControl?.message}
            customClick={workersControl?.onSelectorChange}
          />
          <CustomTextarea
            column="right"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            label={t?.labelResponsibleContact || 'Контакты ответственного лица'}
            placeholder={t?.placeholderResponsibleContact || 'Введите контакты ответственного лица'}
            value={formData.responsibleContact}
            type="textArea"
            name="responsibleContact"
            width="100%"
            height="var(--sadr-font-size-120)"
          />
          <CustomButton
            onClick={handlerOpenModal}
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            text={t?.buttonSaveChanges || 'Сохранить изменения'}
            disabled={isButtonDisabled || !isFormEdit}
            isButton
          />
        </FormContainer>
      )}
    </CustomContainer>
  );
}

export default EditUsageSendStatus;
